import { getCrmColors } from "../../theme/crmColors";
import React, { useEffect, useState, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  IconButton,
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import { head } from "lodash";

const AutoReplySchema = Yup.object().shape({
  keyword: Yup.string().required("Obrigatorio"),
  condition: Yup.string().required("Obrigatorio"),
  response: Yup.string().required("Obrigatorio"),
});

const useStyles = makeStyles((theme) => {
  const isDark = Boolean(theme.replyDark);
  const primary = theme.palette.primary.main;

  return {
    dialogPaper: {
      borderRadius: 10,
      color: isDark ? "#FFFFFF" : "#131b2e",
      backgroundColor: isDark ? getCrmColors(theme, "dark").surface : "#ffffff",
      ...(isDark && { border: `1px solid ${getCrmColors(theme, "dark").border}` }),
      boxShadow: isDark
        ? "0 24px 56px rgba(0, 0, 0, 0.42)"
        : "0 24px 56px rgba(15, 23, 42, 0.16)",
      [theme.breakpoints.down("sm")]: {
        margin: theme.spacing(1.5),
        width: `calc(100% - ${theme.spacing(3)}px)`,
        maxHeight: `calc(100% - ${theme.spacing(3)}px)`,
      },
    },
    dialogTitle: {
      padding: theme.spacing(2.5, 3, 1.5),
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(2, 2, 1.5) },
    },
    form: {
      [theme.breakpoints.down("sm")]: {
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      },
    },
    dialogEyebrow: {
      marginBottom: theme.spacing(0.5),
      color: isDark ? getCrmColors(theme, "dark").accent : "#4c5d8d",
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: 0.5,
      textTransform: "uppercase",
    },
    dialogTitleText: {
      color: isDark ? "#ffffff" : "#131b2e",
      fontSize: 22,
      fontWeight: 800,
      lineHeight: 1.2,
    },
    dialogContent: {
      padding: theme.spacing(1, 3, 2),
      backgroundColor: isDark ? getCrmColors(theme, "dark").surface : "#ffffff",
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(1, 2, 2) },
    },
    field: {
      marginBottom: theme.spacing(1.5),
      "& .MuiInputLabel-root": {
        color: isDark ? "#FFFFFF" : "#4b5563",
        fontWeight: 600,
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: primary,
      },
      ...(isDark && {
        "& .MuiInputLabel-root.Mui-error": { color: theme.palette.error.main },
        "& .MuiInputLabel-root.Mui-disabled": { color: getCrmColors(theme, "dark").muted },
      }),
      "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        color: isDark ? "#ffffff" : "#131b2e",
        backgroundColor: isDark ? getCrmColors(theme, "dark").inset : "#f8fbff",
        "& fieldset": {
          borderColor: isDark
            ? getCrmColors(theme, "dark").border
            : "rgba(195, 198, 214, 0.65)",
        },
        "&:hover fieldset": {
          borderColor: isDark
            ? getCrmColors(theme, "dark").accent
            : "rgba(0, 82, 204, 0.55)",
        },
        "&.Mui-focused fieldset": {
          borderColor: primary,
          borderWidth: 1,
        },
        ...(isDark && {
          "&.Mui-error fieldset": { borderColor: theme.palette.error.main },
          "&.Mui-disabled": { color: getCrmColors(theme, "dark").muted },
        }),
      },
      "& .MuiSelect-icon": {
        color: isDark ? "#FFFFFF" : "inherit",
      },
    },
    attachmentCard: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
      padding: theme.spacing(1),
      borderRadius: 8,
      backgroundColor: isDark ? getCrmColors(theme, "dark").inset : "#f2f6ff",
      ...(isDark && { border: `1px solid ${getCrmColors(theme, "dark").border}` }),
    },
    attachmentButton: {
      justifyContent: "flex-start",
      minWidth: 0,
      color: isDark ? "#FFFFFF" : "#0040a2",
      textTransform: "none",
      fontWeight: 700,
      overflow: "hidden",
      [theme.breakpoints.down("sm")]: {
        flex: 1,
        "& .MuiButton-label": { overflowWrap: "anywhere" },
      },
    },
    deleteMediaButton: {
      color: isDark ? "#fecaca" : "#ba1a1a",
      backgroundColor: isDark ? "rgba(239, 68, 68, 0.16)" : "#ffdad6",
      "&:hover": {
        backgroundColor: isDark ? "rgba(239, 68, 68, 0.24)" : "#ffc7c0",
      },
      [theme.breakpoints.down("sm")]: { flexShrink: 0 },
    },
    dialogActions: {
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 3, 2.5),
      backgroundColor: isDark ? getCrmColors(theme, "dark").surface : "#ffffff",
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(1.5, 2, 2) },
      [theme.breakpoints.down("xs")]: {
        alignItems: "stretch",
        flexDirection: "column",
        "& > *": {
          marginLeft: "0 !important",
        },
      },
    },
    attachAction: {
      marginRight: "auto",
      borderRadius: 8,
      color: isDark ? "#FFFFFF" : "#0040a2",
      borderColor: isDark
        ? getCrmColors(theme, "dark").border
        : "rgba(0, 82, 204, 0.42)",
      textTransform: "none",
      fontWeight: 700,
      "&:hover": {
        borderColor: primary,
        backgroundColor: isDark ? getCrmColors(theme, "dark").hover : "#eef4ff",
      },
      [theme.breakpoints.down("xs")]: {
        marginRight: 0,
      },
    },
    cancelButton: {
      borderRadius: 8,
      color: isDark ? "#FFFFFF" : "#4b5563",
      textTransform: "none",
      fontWeight: 700,
      "&:hover": {
        backgroundColor: isDark ? getCrmColors(theme, "dark").inset : "#f3f4f6",
      },
    },
    saveButton: {
      borderRadius: 8,
      minWidth: 120,
      color: isDark ? getCrmColors(theme, "dark").background : "#ffffff",
      background: isDark ? getCrmColors(theme, "dark").accent : `linear-gradient(135deg, ${primary} 0%, #0052cc 100%)`,
      boxShadow: isDark
        ? "0 10px 22px rgba(0, 0, 0, 0.28)"
        : "0 10px 22px rgba(0, 82, 204, 0.22)",
      textTransform: "none",
      fontWeight: 800,
      "&:hover": {
        background: isDark ? getCrmColors(theme, "dark").focus : "linear-gradient(135deg, #0040a2 0%, #0052cc 100%)",
      },
    },
  };
});

const AutoReplyDialog = ({ open, onClose, recordId, reload }) => {
  const classes = useStyles();
  const initialState = {
    keyword: "",
    condition: "equal",
    response: "",
    mediaPath: null,
    mediaName: null,
  };
  const [autoReply, setAutoReply] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const attachmentFile = useRef(null);
  const saving = useRef(false);
  const savedRecordId = useRef(null);
  const savedRecordRevision = useRef(null);

  useEffect(() => {
    if (!open || !recordId) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/auto-replies/${recordId}`);
        if (active) setAutoReply(data);
      } catch (err) {
        if (active) toastError(err);
      }
    })();
    return () => { active = false; };
  }, [recordId, open]);

  const handleClose = () => {
    if (saving.current) return;
    savedRecordId.current = null;
    savedRecordRevision.current = null;
    setAutoReply(initialState);
    setAttachment(null);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSave = async (values) => {
    if (saving.current) return;
    saving.current = true;
    let completed = false;
    const autoReplyData = {
      keyword: values.keyword,
      condition: values.condition,
      response: values.response,
    };

    try {
      let targetId = recordId || savedRecordId.current;
      let revision = savedRecordRevision.current || autoReply.revision;
      if (targetId) {
        const { data } = await api.put(`/auto-replies/${targetId}`, autoReplyData, { headers: revision ? { "If-Match": revision } : {} });
        revision = data.revision;
      } else {
        const { data } = await api.post("/auto-replies", autoReplyData);
        // Keep the created record when the following upload needs another try.
        targetId = data.id;
        savedRecordId.current = targetId;
        revision = data.revision;
      }
      savedRecordRevision.current = revision;
      if (attachment) {
        const formData = new FormData();
        formData.append("file", attachment);
        const { data } = await api.post(`/auto-replies/${targetId}/media-upload`, formData, { headers: revision ? { "If-Match": revision } : {} });
        savedRecordRevision.current = data.record?.revision || revision;
      }
      toast.success(i18n.t("autoReplies.toasts.success"));
      if (typeof reload === "function") {
        reload();
      }
      completed = true;
    } catch (err) {
      toastError(err);
    } finally {
      saving.current = false;
    }
    if (completed) handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      if (attachmentFile.current) {
        attachmentFile.current.value = null;
      }
    }

    if (autoReply.mediaPath) {
      const revision = savedRecordRevision.current || autoReply.revision;
      const { data } = await api.delete(`/auto-replies/${autoReply.id}/media-upload`, { headers: revision ? { "If-Match": revision } : {} });
      savedRecordRevision.current = data.record?.revision || revision;
      setAutoReply((prev) => ({
        ...prev,
        ...(data.record || {}),
        mediaPath: null,
        mediaName: null,
      }));
      toast.success(i18n.t("autoReplies.toasts.deleted"));
      if (typeof reload === "function") {
        reload();
      }
    }
  };

  return (
    <div>
      <ConfirmationModal
        title={i18n.t("autoReplies.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("autoReplies.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Box className={classes.dialogEyebrow}>Resposta automatica</Box>
          <Box className={classes.dialogTitleText}>
            {recordId
              ? i18n.t("autoReplies.dialog.edit")
              : i18n.t("autoReplies.dialog.new")}
          </Box>
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={autoReply}
          enableReinitialize
          validationSchema={AutoReplySchema}
          onSubmit={(values) => handleSave(values)}
        >
          {({ values, errors, touched, handleChange, isSubmitting }) => (
            <Form className={classes.form}>
              <DialogContent className={classes.dialogContent}>
                <TextField
                  className={classes.field}
                  label={i18n.t("autoReplies.form.keyword")}
                  name="keyword"
                  disabled={isSubmitting}
                  value={values.keyword}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  size="small"
                  error={touched.keyword && Boolean(errors.keyword)}
                />
                <TextField
                  className={classes.field}
                  select
                  label={i18n.t("autoReplies.form.condition")}
                  name="condition"
                  disabled={isSubmitting}
                  value={values.condition}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  size="small"
                  error={touched.condition && Boolean(errors.condition)}
                >
                  <MenuItem value="equal">Igual</MenuItem>
                  <MenuItem value="contains">Contem</MenuItem>
                  <MenuItem value="startsWith">Comeca com</MenuItem>
                  <MenuItem value="endsWith">Termina com</MenuItem>
                </TextField>
                <TextField
                  className={classes.field}
                  label={i18n.t("autoReplies.form.response")}
                  name="response"
                  disabled={isSubmitting}
                  value={values.response}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={4}
                  margin="dense"
                  variant="outlined"
                  error={touched.response && Boolean(errors.response)}
                />
                {(autoReply.mediaPath || attachment) && (
                  <Box className={classes.attachmentCard}>
                    <Button
                      className={classes.attachmentButton}
                      startIcon={<AttachFileIcon />}
                      onClick={async () => {
                        if (!autoReply.id) return;
                        try {
                          const { data } = await api.get(`/auto-replies/${autoReply.id}/media`, {
                            responseType: "blob",
                          });
                          const url = window.URL.createObjectURL(new Blob([data]));
                          const link = document.createElement("a");
                          link.href = url;
                          link.setAttribute("download", attachment ? attachment.name : (autoReply.mediaName || "arquivo"));
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        } catch (e) {
                          toastError(e);
                        }
                      }}
                    >
                      {attachment ? attachment.name : autoReply.mediaName}
                    </Button>
                    <IconButton
                      className={classes.deleteMediaButton}
                      disabled={isSubmitting}
                      onClick={() => setConfirmationOpen(true)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                )}
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                {!attachment && !autoReply.mediaPath && (
                  <Button
                    className={classes.attachAction}
                    disabled={isSubmitting}
                    onClick={() => attachmentFile.current.click()}
                    color="primary"
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                  >
                    {i18n.t("quickMessages.buttons.attach")}
                  </Button>
                )}
                <Button onClick={handleClose} disabled={isSubmitting} className={classes.cancelButton}>
                  {i18n.t("autoReplies.buttons.cancel")}
                </Button>
                <Button type="submit" disabled={isSubmitting} color="primary" variant="contained" className={classes.saveButton}>
                  {i18n.t("autoReplies.buttons.save")}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default AutoReplyDialog;
